import { useMutation } from '@apollo/client';
import type { CommunityWorkflowConfiguration, WorkflowType } from '@communities/content-management/domain';
import { UPSERT_COMMUNITY_WORKFLOWS, GET_COMMUNITY_WORKFLOWS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface UpsertCommunityWorkflowResponse {
  upsertCommunityWorkflowConfiguration: CommunityWorkflowConfiguration;
}

export function useUpsertCommunityWorkflow() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updateWorkflowMutation, { loading }] = useMutation<UpsertCommunityWorkflowResponse>(UPSERT_COMMUNITY_WORKFLOWS);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const upsertWorkflow = async (communityId: string, type: WorkflowType, workflow: CommunityWorkflowConfiguration) => {
    try {
      track('UpsertCommunityWorkflowInitiated', {
        communityId,
        workflowType: type,
      });

      // Create the workflow configuration to send to the API
      // Note: costPerAudioHour and numberOfClipsPerHour are query-only fields and should not be included in updates
      const completeWorkflow = {
        topicActions: workflow.topicActions || [],
        apiSettings: workflow.apiSettings
          ? {
              maxTps: workflow.apiSettings.maxTps || 100,
              monthlyBudget: workflow.apiSettings.monthlyBudget || 5000,
              accessClients: workflow.apiSettings.accessClients || [],
              ...(workflow.apiSettings.contentTypeTps && { contentTypeTps: workflow.apiSettings.contentTypeTps }),
            }
          : {},
      };

      await updateWorkflowMutation({
        variables: {
          communityId,
          type,
          input: completeWorkflow,
        },
        refetchQueries: communityId ? [{ query: GET_COMMUNITY_WORKFLOWS, variables: { communityId } }] : [],
        awaitRefetchQueries: true,
      });

      track('UpsertCommunityWorkflowSucceeded', {
        communityId,
        workflowType: type,
      });

      return true;
    } catch (error) {
      track('UpsertCommunityWorkflowFailed', {
        communityId,
        workflowType: type,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    upsertWorkflow,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpsertCommunityWorkflow;
